import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PerformanceInsights = ({performanceData}) => {
    const maxRevenue = performanceData.trendingRevenue?.length > 0 ? Math.max(...performanceData.trendingRevenue.map(d => d.revenue)) : 0;
    const minRevenue = performanceData.trendingRevenue?.length > 0 ? Math.min(...performanceData.trendingRevenue.map(d => d.revenue)) : 0;

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Performance insights</h2>
                <button
                    className="flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                    <i className="fa fa-calendar"></i>
                    <span className="font-medium">Last 7 Days</span>
                    <i className="fa fa-chevron-down text-xs"></i>
                </button>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Total Conversations</h3>
                    <p className="text-4xl font-bold text-gray-900 mb-1">{performanceData.totalConversations}</p>
                    <p className="text-sm text-gray-500">in the last 7 days</p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Revenue Generated</h3>
                    <p className="text-4xl font-bold text-gray-900 mb-1">${(performanceData.revenueGenerated || 0).toFixed(2)}</p>
                    <p className="text-sm text-gray-500">${(performanceData.revenuePerCall || 0).toFixed(2)} per
                        call</p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Success Rate</h3>
                    <p className="text-4xl font-bold text-gray-900 mb-1">{performanceData.successRate}%</p>
                    <p className="text-sm text-gray-500">across all objectives</p>
                </div>
            </div>

            {/* Trending Revenue Chart */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Trending Revenue</h3>
                <div className="h-80">
                    {performanceData.trendingRevenue?.length > 0 ? (
                        <Line
                            data={{
                                labels: performanceData.trendingRevenue.map(d => {
                                    const date = new Date(d.date);
                                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                }),
                                datasets: [
                                    {
                                        label: 'Revenue',
                                        data: performanceData.trendingRevenue.map(d => d.revenue),
                                        borderColor: '#3B82F6',
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                        borderWidth: 2,
                                        pointBackgroundColor: '#3B82F6',
                                        pointBorderColor: '#ffffff',
                                        pointBorderWidth: 2,
                                        pointRadius: 4,
                                        pointHoverRadius: 6,
                                        fill: true,
                                        tension: 0.4
                                    }
                                ]
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: false
                                    },
                                    tooltip: {
                                        backgroundColor: '#1F2937',
                                        titleColor: '#ffffff',
                                        bodyColor: '#ffffff',
                                        borderColor: '#374151',
                                        borderWidth: 1,
                                        cornerRadius: 6,
                                        callbacks: {
                                            label: function(context) {
                                                return `$${context.parsed.y.toLocaleString()}`;
                                            }
                                        }
                                    }
                                },
                                scales: {
                                    x: {
                                        grid: {
                                            display: false
                                        },
                                        border: {
                                            display: false
                                        }
                                    },
                                    y: {
                                        grid: {
                                            color: '#F3F4F6'
                                        },
                                        border: {
                                            display: false
                                        },
                                        ticks: {
                                            callback: function(value) {
                                                return '$' + value.toLocaleString();
                                            }
                                        }
                                    }
                                }
                            }}
                        />
                    ) : (
                        <div className="h-full flex items-center justify-center">
                            <p className="text-gray-500">No revenue data available</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Success Criteria Performance */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Success Criteria Performance</h3>
                <div className="space-y-6">
                    {performanceData.successCriteria?.map((criteria, index) => {
                        const percentage = criteria.target ? Math.round((criteria.value / criteria.target) * 100) : criteria.value;
                        const color = percentage >= 100 ? '#10B981' : percentage >= 80 ? '#F59E0B' : '#EF4444';

                        return (
                            <div key={index}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-gray-700">{criteria.name}</span>
                                    <span
                                        className="px-3 py-1 rounded text-sm font-bold text-white"
                                        style={{backgroundColor: color}}
                                    >
                    {criteria.value}{criteria.name === 'Customer Satisfaction' ? '/5' : `/${criteria.target}`}
                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div
                                        className="h-3 rounded-full transition-all duration-500"
                                        style={{
                                            width: `${Math.min(percentage, 100)}%`,
                                            backgroundColor: color
                                        }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default PerformanceInsights;